/* PROCESS:
   • Click the manual entry button to go to manual entry page
     Required if there is no spindle and/or cone label to scan
     or if one or both labels are unreadable
 OR
   • Scan a machine spindle QR code to obtain machine info
   • Scan the cone QR code to obtain the cone info
   • Save record as "initialized" to the database
   • Reload the page to initial stage
 OR
   • Scan the cone QR code and try to retrieve the production record based on the cone # scanned
   • If found, allow the user to complete the previously initiated record
      • Select if the item scanned is "GOOD" or "BAD"
         • If BAD, select a defect type.
      • Enter the package weight
      • Complete or cancel updating of the record
*/
import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Input, Row } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { FilterSelect, QrReader, ServiceThreadSpinner } from '.';
import { SpinnerButton } from './';
import { DesktopContainer } from '../layouts';
import { api, constants, filterHelpers, storage, UserContext } from '../utils';
import _ from "lodash";

export default function ScanProduction() {
   const { currentUser } = useContext(UserContext);
   const navigate = useNavigate();
   const sleep = ms => new Promise(r => setTimeout(r, ms));
   const ALL_OPTS = { value: 0, label: "-- Select --" };
   const INIT_MODEL = {
      id: 0, 
      coneId: 0,
      defectId: 0, // not defined yet
      isExceptionMachine: true,
      isGood: false,
      item: "",
      machineId: 0,
      machineNum: 0,
      maxOverage: 0,
      maxWeight: 0,
      minWeight: 0,
      pkgWeight: 0,
      side: "N/A",
      spindle: 0
   };
   const [filters, setFilters] = useState([
      { filterName: "defectId", value: ALL_OPTS },
      { filterName: 'isGood', value: false },
      { filterName: 'weight', value: 0 }
   ]);
   const isGood = filterHelpers.getValue(filters, "isGood");
   const weight = filterHelpers.getValue(filters, "weight");
   const [data, setData] = useState("");            // machine label or Cone label scanned QR code string
   const [defects, setDefects] = useState([]);      // Defect types for dropdown
   const [disabled, setDisabled] = useState(false); // Disable good checkbox and defect list when appropriate
   const [model, setModel] = useState(INIT_MODEL);  // Production Record to be saved
   const [msg, setMsg] = useState("Scan a machine spindle label QR Code");
   const [openQr, setOpenQr] = useState(false);     // Shows or hides QR scanner component
   const [saving, setSave] = useState(false);       // Show or hide spinner on spinner buttons
   const [spin, setSpinner] = useState(true);       // Shows or hides spinner component
   const [step, setStep] = useState(1);             // 1=scan machine label, 2=scan cone label, 3=user input
   let savedDevice = JSON.parse(storage.getItem("deviceType"));
   const hasScanner = savedDevice.hasScanner;

   useEffect(() => {
      api
         .fetch(`Production/LoadDefectTypes`)
         .then((response) => {
            if (response.data.success) {
               const defectList = _.concat(
                  [ALL_OPTS],
                  _.map(response.data.defects, (d) => ({
                    value: d.id,
                    label: d.defect,
                    isDisabled: d.id === 9 // Under MSR Weight is auto-selected, operator cannot select
               })));
               return { defectList: defectList };
            } else {
               throw new Error(response);
            }
         })
         .then(({ defectList }) => {
             setDefects(defectList);
         })
         .catch((error) => {
           api.catchHandler(error);
           toast.error(error);
         })
         .finally(() => {
            setSpinner(false);
            if (hasScanner) inputFocus("scanInput");
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentUser])

   useEffect(() => {
      if (model && model.isExceptionMachine === false) { // Not an exception machine
         // Over MSR Weight defect is auto-selected only, operator cannot select
         setDefects(defects.map(item => item.value === 5 ? { ...item, isDisabled: true } : item))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [model]);

   useEffect(() => {
      if (step === 1) {
         setMsg("Scan a machine spindle label");
         if (hasScanner) inputFocus("scanInput");
      }
      else if (step === 2) {
         setMsg("Scan a cone label");
         if (hasScanner) inputFocus("scanInput");
      }
      else setMsg("Enter required data");

      if (step === 4)
         inputFocus("pkgWgt");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [step])

   useEffect(() => {
      if (data && data.length > 0) {
         // a scan was completed
         const values = data.split(",");
         if (Array.isArray(values)) {
            if (step === 1) {
               if(values.length === 5 && !isNaN(+values[0])) { // Machine ID should be numeric
                  let changes = Object.assign({}, model);
                  changes.machineId = values[1];
                  changes.machineNum = values[2];
                  changes.side = values[3].startsWith("N") ? "N/A" : values[3];
                  changes.spindle = values[4];
                  setModel(changes);
                  setStep(2);
               }
               else {
                  // Test if user scanned a cone label to complete it. Item number should NOT be numeric.
                  if (values.length === 2 && isNaN(+values[0]))
                     runQuery(values[1]); 
                  else
                     setMsg("Read error: invalid machine spindle label");
               }
            }
            else {
               // cone label info
               if(values.length === 2 && isNaN(+values[0])) { // Item number should NOT be numeric
                  let changes = Object.assign({}, model);
                  changes.item = values[0];
                  changes.coneId = values[1];
                  setModel(changes);
                  setStep(3);
               }
               else {
                  // Do NOT put an error message here. There are too many reports of the first scan for the
                  // spindle label reading twice on handheld devices causing the error message to be shown
                  // simply because the initial scanned value was returned mutliple times instead of once.
                  //setMsg("Read error: invalid cone label");
               }
            }
         }
         setData("");
      }
      if (hasScanner) inputFocus("scanInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data])

   function cancelRecord()  {
      window.location.reload(); // Reload the page
   }
   
   const handleFocus = (event) => event.target.select();

   function handleQrScan(val) {
      setOpenQr(false);  // Hide QR Scanner
      setData(val);
   }

   function handleQrScanFailure(val) {
      setOpenQr(false);  // Hide QR Scanner
      toast.warning("This device does not have a camera.");
   }

   async function inputFocus(fieldname) {
      await sleep(50);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   function manualRecord() {
      var nav = constants.ENDPOINTS.newProdRecord;
      navigate(nav);
   }

   function onDefectChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      if (newFilterValue.value < 1)
         changes.defectId = 1;
      else
         changes.defectId = newFilterValue.value;
      setModel(changes);
      inputFocus("pkgWgt");
   }

   function onFilterChange(changedFilter) {
      const filterChanges = filterHelpers.getFilters(filters, changedFilter);
      setFilters(filterChanges);
   }

   function onGoodVsBad(opt) {
      let changes = Object.assign({}, model);
      changes.isGood = opt;
      setModel(changes);
      onFilterChange({ filterName: "isGood", value: opt, filteredDisplay: '' })
      inputFocus("pkgWgt");
   }

   function onWeightChange(val) {
      if (val === undefined || val === null || val === "") val = 0;

      let changes = Object.assign({}, model);
      if (val < changes.minWeight) {
         // operator may still be updating, but we need to enforce this until then
         resetWeight(9, false, val, true);
         changes.isGood = false;
         changes.defectId = 9;
         changes.defect = "Under MSR Weight";
      }
      else if (val > changes.maxWeight) {
         // need to enforce the overweight issue
         resetWeight(5, false, val, true);
         changes.isGood = false;
         changes.defectId = 5;
         changes.defect = "Over MSR Weight";
      }
      else {
         // operator may still be updating, but for now it is within spec
         resetWeight(0, true, val, false);
         changes.isGood = true;
         changes.defectId = 1;
         changes.defect = "Good";
      }
      changes.pkgWeight = val;
      setModel(changes);
      inputFocus("pkgWgt");
   }

   function resetWeight(dfctId, goodVal, wgtVal, isDisabled) {
      const dfct = _.find(defects, (x) => x.value === dfctId);
      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "defectId",
         value: dfct,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "isGood",
         value: goodVal,
         filteredDisplay: ''
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "weight",
         value: wgtVal,
      });
      setFilters(newFilters);
      setDisabled(isDisabled);
   }

   function runQuery(coneId) {
      // The user has scanned a cone label without the spindle label being first
      // We need to see if the record exists and can be completed at this time
      let lookup = false;
      if (coneId && coneId > 0) {
         api
            .fetch(`Production/GetProductionRecordByConeId/${coneId}/${lookup}`)
            .then(r => {
               if (r.data.success) {
                  if (r.data.message !== null && r.data.message !== undefined) {
                     setModel(r.data.message);
                     setStep(4);
                  } else {
                     toast.error("No data returned!");
                  }
               } else {
                  toast.error(r.data.message);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
      }
      else {
         setMsg("Read error: not a valid spindle or cone label");
      }
   }

   function saveRecord() {
      if (model.machineId === 0 || isNaN(model.machineId)) {
         toast.error("Machine info missing or invalid!");
         setModel(INIT_MODEL);
         setStep(1);
         return;
      }
      if (model.coneId === 0 || typeof model.item !== 'string' || !isNaN(+model.item)) {
         toast.error("Cone/Item info missing or invalid!");
         let changes = Object.assign({}, model);
         changes.item = "";
         changes.coneId = 0;
         setModel(changes);
         setStep(2);
         return;
      }

      setSave(true);
      api
         .post(`Production/SaveConeRecord`, model)
         .then(response => {
            if (response.data.success) {
               toast.success("Production recorded successfully");
               cancelRecord();
            }
            else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setSave(false);
         });
   }

   function updateRecord() {
      if (model.id === 0) {
         toast.error("Record identifier missing!");
         return;
      }

      let failures = [];
      if (model.pkgWeight <= 0) {
         failures.push("Package weight is required\n");
      }
      else if (model.pkgWeight > model.maxOverage) {
         failures.push("The max weight limit has been exceeded! Please check your package weight.\n");
      }
      if (isGood === false && model.defectId === 1) {
         failures.push("Defect is required\n");
      }
      if (failures.length) {
         toast.error(failures.join(''));
         return;
      }

      setSave(true);
      api
         .post(`Production/CompleteConeRecord`, model)
         .then(response => {
            if (response.data.success) {
               toast.success("Production recorded complete!");
               cancelRecord();
            }
            else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setSave(false);
         });
   }

   return (
      <DesktopContainer screenName="Production Recording">
         <ServiceThreadSpinner on={spin}>
            {/* Initial requirements
                The user must choose manual data entry or start scanning QR code labels */}
            {step < 3 ? (
               <div className="filter-header">
                  <Row className="justify-content-center">
                     <Col className="h5 fw-bold my-0" xs={11}>
                        Step {step}: {msg}
                     </Col>
                  </Row>
                  <Row className="justify-content-center">
                     <Col xs="11">
                        <Button
                           title="newRecord"
                           className="shredButton w-100 btn-md my-3"
                           onClick={() => { manualRecord(); }}>
                           Manual Entry
                        </Button>
                     </Col>
                  </Row>
                  {!hasScanner ? ( // NOT a handheld device
                     <Row className="justify-content-center">
                        <Col xs={11}>
                           <Button className="shredButton w-100 btn-md my-3" onClick={() => setOpenQr(!openQr)}>
                              {openQr ? "Hide Scanner" : "Scan QR Code"}
                           </Button>
                        </Col>
                     </Row>
                  ) : null }
                  {hasScanner ? ( // IS a Handheld device
                     <div>
                        <Row className="lineheight8">&nbsp;</Row>
                        <Row className="justify-content-center">
                           <Col xs="11">
                              <Input
                                 name="scanInput"
                                 placeholder="Scan QR code"
                                 className="h-100"
                                 value={data}
                                 onFocus={handleFocus}
                                 onChange={(e) => handleQrScan(e.target.value)}
                              />
                           </Col>
                        </Row>
                     </div>
                  ) : null }
               
                  {openQr && <QrReader callback={handleQrScan} callbackError={handleQrScanFailure} />}
               </div>
            ) : null}

            {/* Save and Cancel Buttons ONLY - initiating new record */}
            {step === 3 ? (
               <div className="filter-header">
                  <Row className="lineheight8">&nbsp;</Row>
                  <Row className="align-items-center my-1 mx-2">
                     <Col xs={12}>
                        <SpinnerButton
                           title="Save"
                           className="shredButton btn-md w-100"
                           onClick={() => saveRecord()}
                           on={saving}
                           disabled={saving}
                        > Save
                        </SpinnerButton>
                     </Col>
                  </Row>

                  <Row className="lineheight8">&nbsp;</Row>
                  <Row className="align-items-center my-1 mx-2">
                     <Col xs={12}>
                        <SpinnerButton
                           title="Clear"
                           className="btn btn-md w-100"
                           onClick={() => cancelRecord()}
                           on={saving}
                           disabled={saving}
                        > Cancel
                        </SpinnerButton>
                     </Col>
                  </Row>
               </div>
            ) : null}

            {/* Requirements to complete an existing record */}
            {step === 4 ? (
               <div className="filter-header">
                  {/* Display Cone */}
                  <Row className="lineheight8">&nbsp;</Row>
                  <Row className="align-items-center my-1 mx-2">
                     <Col className="h6 fw-bold my-0" xs={12}>
                        Cone #: {model.coneId}
                     </Col>
                  </Row>

                  {/* Display Item */}
                  <Row className="lineheight8">&nbsp;</Row>
                  <Row className="align-items-center my-1 mx-2">
                     <Col className="h6 fw-bold my-0" xs={12}>
                        Item:
                     </Col>
                  </Row>
                  <Row className="align-items-center my-1 mx-2">
                     <Col className="h6 my-0" xs={12}>
                        {model.itemWithDesc}
                     </Col>
                  </Row>

                  {/* Display Machine */}
                  <Row className="lineheight8">&nbsp;</Row>
                  <Row className="align-items-center my-1 mx-2">
                     <Col className="h6 fw-bold my-0" xs={12}>
                        Machine:
                     </Col>
                  </Row>
                  <Row className="align-items-center my-1 mx-2">
                     <Col className="h6 my-0" xs={12}>
                        {model.machineDisplay}
                     </Col>
                  </Row>

                  {/* Identify pkg weight being recorded */}
                  <Row className="lineheight8">&nbsp;</Row>
                  <Row className="align-items-center my-1 mx-2">
                     <Col className="h6 fw-bold my-0" xs={12}>
                        Package Weight:
                     </Col>
                  </Row>
                  <Row className="align-items-center my-1 mx-2">
                     <Col xs="12">
                        <Input className="no-spinner h-100"  // This className hides the up/down arrows at end of entry field
                           type="number"
                           name="pkgWgt"
                           value={weight}
                           onFocus={handleFocus}
                           onChange={(e) => {
                              if (e.target.value.match(/^\d{1,3}(\.\d{0,2})?$/)) // Numeric 1-3 digits and up to 2 decimals allowed
                                 onWeightChange(e.target.value);
                           }}
                        />
                     </Col>
                  </Row>

                  {/* Identify a GOOD vs. BAD item being recorded */}
                  <Row className="lineheight15">&nbsp;</Row>
                  <Row className="align-items-center my-1 mx-2">
                     <Col xs={12}>
                        <Input
                           type="checkbox"
                           value={isGood}
                           className="h6 fw-bold my-0 ms-1 mx-2"
                           onChange={() => onGoodVsBad(!isGood)}
                           checked={isGood}
                           disabled={disabled}
                        />
                        <span className="h6 fw-bold ps-2">Item produced is good</span>
                     </Col>
                  </Row>

                  {/* If item is BAD then a defect type must be selected */}
                  {isGood === false ? (
                     <div>
                        <Row className="align-items-center my-1 mx-2">
                           <Col className="h6 fw-bold my-0" xs={12}>
                              Defect:
                           </Col>
                        </Row>
                        <Row className="align-items-center my-1 mx-2">
                           <Col xs={12}>
                              <FilterSelect
                                 filterName="defectId"
                                 options={defects}
                                 value={filterHelpers.getValue(filters, "defectId")}
                                 onChangeCallback={(e) => onDefectChange(e)}
                                 disabled={disabled}
                              />
                           </Col>
                        </Row>
                     </div>
                  ) : null}

                  {/* Update and Cancel Buttons */}
                  <Row className="lineheight8">&nbsp;</Row>
                  <Row className="align-items-center my-1 mx-2">
                     <Col xs={12}>
                        <SpinnerButton
                           title="Complete"
                           className="shredButton btn-md w-100"
                           onClick={() => updateRecord()}
                           on={saving}
                           disabled={saving}
                        > Complete
                        </SpinnerButton>
                     </Col>
                  </Row>

                  <Row className="lineheight8">&nbsp;</Row>
                  <Row className="align-items-center my-1 mx-2">
                     <Col xs={12}>
                        <SpinnerButton
                           title="Cancel"
                           className="btn btn-md w-100"
                           onClick={() => cancelRecord()}
                           on={saving}
                           disabled={saving}
                        > Cancel
                        </SpinnerButton>
                     </Col>
                  </Row>
               </div>
            ) : null}
         </ServiceThreadSpinner>
      </DesktopContainer >
   );
}
